import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IReportingPeriod } from "../../model/reporting/ReportingPeriod";
import { IReportingPeriodFormType } from "../../model/reporting/ReportingPeriodFormType";
import GetReportingPeriodFormTypesInteractor from "../../interactor/reporting/GetReportingPeriodFormTypesInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import ReportingPeriodPresentationModel from "../reporting/model/ReportingPeriodPresentationModel";
import ReportingPeriodFormTypePresentationModel from "../../model/form/mappers/ReportingPeriodFormTypeListMapper";
import AddNewDraftFormVersionInteractor from "../../interactor/form/AddNewDraftFormVersionInteractor";

export interface TFormsPresenter extends TLoadingAwarePresenter {
  reportingPeriods?: IReportingPeriod[];
  formTypes?: IReportingPeriodFormType[];
  isPreviousForms?: boolean;
  expandedReportingPeriodId?: number;
}

export interface IFormsPresenter extends TFormsPresenter, TPresentable {
  setReportingPeriods(reportingPeriods: IReportingPeriod[]): void;
  setFormTypes(reportingPeriodFormTypes: IReportingPeriodFormType[]): void;
  onReportingPeriodRowExpanded(expanded: any, record: any): void;
  addNewDraftFormVersion(
    reportingPeriodApplicantId: number,
    reportingPeriodFormTypeId: number
  ): Promise<IReportingPeriodFormType>;
}

const defaultState: TFormsPresenter = {};

const FormsPresenter = withStore<IFormsPresenter, TFormsPresenter>(
  ({ application, store, loader, translate }): IFormsPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TFormsPresenter>();

    loader.start("formsLoader");

    const setReportingPeriods = (reportingPeriods: IReportingPeriod[]) => {
      _store.update({
        reportingPeriods: reportingPeriods
          ? reportingPeriods.map(ReportingPeriodPresentationModel)
          : []
      });
      loader.stop("formsLoader");
    };

    const setFormTypes = (
      reportingPeriodFormTypes: IReportingPeriodFormType[]
    ) => {
      const isPreviousForms =
        _store.getState<TFormsPresenter>().isPreviousForms;
      _store.update({
        formTypes: reportingPeriodFormTypes
          ? reportingPeriodFormTypes.map((item) =>
              ReportingPeriodFormTypePresentationModel(
                item,
                isPreviousForms,
                translate
              )
            )
          : []
      });
    };

    const getFormTypes = async (reportingPeriodApplicantId: number) => {
      try {
        let reportingPeriodFormTypes = await _application.container
          .resolve<GetReportingPeriodFormTypesInteractor>(
            "getReportingPeriodFormTypes"
          )
          .execute(reportingPeriodApplicantId);
        setFormTypes(reportingPeriodFormTypes);
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const onReportingPeriodRowExpanded = async (expanded: any, record: any) => {
      if (expanded) {
        loader.start("formTypesLoader");
        await getFormTypes(record.id);
        _store.update({
          expandedReportingPeriodId: record.reportingPeriodId
        });
        loader.stop("formTypesLoader");
      }
    };

    const addNewDraftFormVersion = async (
      reportingPeriodApplicantId: number,
      reportingPeriodFormTypeId: number
    ) => {
      let formTypes = _store.getState<TFormsPresenter>().formTypes || [];
      let formType =
        formTypes.find(
          (formType) => formType.id === reportingPeriodFormTypeId
        ) || ({} as IReportingPeriodFormType);

      loader.start("formNewVersionLoader");

      await application.container
        .resolve<AddNewDraftFormVersionInteractor>("addNewDraftFormVersion")
        .execute(reportingPeriodApplicantId, reportingPeriodFormTypeId);
      await getFormTypes(reportingPeriodApplicantId);

      formTypes = _store.getState<TFormsPresenter>().formTypes || [];
      formType =
        formTypes.find(
          (formType) => formType.id === reportingPeriodFormTypeId
        ) || ({} as IReportingPeriodFormType);

      loader.stop("formNewVersionLoader");
      return formType;
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      setReportingPeriods,
      setFormTypes,
      onReportingPeriodRowExpanded,
      addNewDraftFormVersion
    };
  },
  defaultState
);

export default FormsPresenter;
